import React from "react";

import { getSingleAssetMarketView } from "../../api/client";

/**
 * 右侧行情的两条加载路径，集中在一处。
 *
 * ① URL 带 `?asset=` 进来时，先把该标的的行情取回来；
 * ② 绑定的核算目标被删空时，右侧回落到该标的的行情态
 *    （产品口径 2026-08-24：删空之后右侧就变成行情态）。
 * ③ 绑定还**没有物化**（create_on_write，一笔都没写过）时同样回落到行情态。
 *    ②只覆盖"写过又删空"，覆盖不到"从没写过"：那种目标的绑定视图 calculation 是 null，
 *    accountingViewFromBinding 直接返回 null，②的判据（rows 是空数组）永远不成立。
 *    于是从概览「新增持仓」进来、把弹窗关掉之后，右侧一片空白、连「记录交易」都没有，
 *    只能退回概览重走一遍（2026-09-04 用户提出）。
 *
 * 第二条是 AGENT-040-c 的后半段。前半段（不再拿删除前的旧结果兜底）在
 * `singleAssetViewState.js`；只做前半段的话右侧会变成空白而不是行情。
 *
 * 从 SingleAssetAgentDev.jsx 抽出来是因为那个文件已经贴着 900 行上限。
 */
export function useSingleAssetMarketView({
  initialAssetQuery,
  activeBinding,
  directAccountingView,
  loadedMarketAssetRef,
  setDirectMarketView,
  onError,
}) {
  const loadingMarketAssetRef = React.useRef(null);
  const requestSequenceRef = React.useRef(0);
  const load = React.useCallback((asset) => {
    const code = String(asset || "").trim();
    const normalizedCode = code.toUpperCase();
    if (
      !code
      || loadedMarketAssetRef.current === normalizedCode
      || loadingMarketAssetRef.current === normalizedCode
    ) return undefined;
    loadingMarketAssetRef.current = normalizedCode;
    const sequence = ++requestSequenceRef.current;
    void getSingleAssetMarketView(code).then((marketView) => {
      if (sequence !== requestSequenceRef.current) return;
      loadedMarketAssetRef.current = marketView.asset_code;
      setDirectMarketView(marketView);
    }).catch((error) => {
      if (sequence === requestSequenceRef.current) {
        onError?.(error.message || "暂时无法打开该标的行情。");
      }
    }).finally(() => {
      if (
        sequence === requestSequenceRef.current
        && loadingMarketAssetRef.current === normalizedCode
      ) {
        loadingMarketAssetRef.current = null;
      }
    });
    // Do not cancel on React StrictMode's development-only effect replay. The
    // sequence guard above still rejects an actually superseded asset request.
    return undefined;
  }, [loadedMarketAssetRef, setDirectMarketView, onError]);

  React.useEffect(() => load(initialAssetQuery), [initialAssetQuery, load]);

  // 绑定事实确认为空（不是"还没取回来"）时，把行情补上。
  const emptyBoundAsset = (directAccountingView
    && (directAccountingView.rows || []).length === 0)
    && !directAccountingView.opening_position
    ? String(directAccountingView.target?.asset_code || "")
    : "";
  React.useEffect(() => load(emptyBoundAsset), [emptyBoundAsset, load]);

  // 还没物化的绑定：确定是空的，不是"还没取回来"——不会有闪烁。
  const unmaterialisedAsset = String(activeBinding?.write_mode || "") === "create_on_write"
    ? String(activeBinding?.asset_code || "")
    : "";
  React.useEffect(() => load(unmaterialisedAsset), [unmaterialisedAsset, load]);
}
